<template>
  <loader v-if="loading" :stretch="true" :inverse="true" />
  <template v-else>
    <StudentChoiceHeader :style="themeStyle"></StudentChoiceHeader>
    <div class="application-page" :style="themeStyle">
      <input type="hidden" id="lid" name="lid" :value="lid" />
      <input type="hidden" id="productTypeId" name="productTypeId" :value="productTypeId" />
      <input type="hidden" id="programTypeId" name="programTypeId" :value="programTypeId" />
      <input type="hidden" id="leadSourceId" name="leadSourceId" :value="leadSourceId" />
      <input type="hidden" id="applicationTypeId" name="applicationTypeId" :value="applicationTypeId" />
      <div class="application-ad">
        <h1 v-if="error">Something has gone wrong.</h1>
        <slot v-else name="ad"></slot>
      </div>
      <div v-if="error" class="error-message">
        <p>
          We apologize, but we are not able to process your request at this time. If you continue to receive this
          message please contact support.
        </p>
      </div>
      <slot v-else></slot>
    </div>
    <div v-if="lid > 0" class="support-wrapper" :style="themeStyle">
      <a title="Support" @click="showSupportModal = true" href="#" class="support">
        <font-awesome-layers>
          <font-awesome-icon icon="fa-solid fa-circle" class="circle" />
          <font-awesome-icon
            icon="fa-solid fa-headset"
            transform="shrink-6"
            class="icon"
            :style="{ color: 'white' }"
          /> </font-awesome-layers
      ></a>
    </div>
    <transition name="modal">
      <support-modal :style="themeStyle" id="supportModal" @close="showSupportModal = false" v-if="showSupportModal" />
    </transition>
    <div class="application-page-footer" :style="themeStyle">
      <StudentChoiceFooter :external-links="externalLinks" :lender-id="lenderData?.lid"></StudentChoiceFooter>
    </div>
  </template>
</template>
<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import { useStore } from "vuex";
import useFormatter from "@/use/formatter";
import useApplication from "@/use/application";
import { useRoute, useRouter } from "vue-router";
import { $api } from "@/services/api";
import Loader from "@/components/shared/Loader.vue";
import StudentChoiceFooter from "@/components/shared/layout/FooterRefi.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import SupportModal from "@/components/modals/SupportModal.vue";
import type { AppData } from "@/models/application";
import StudentChoiceHeader from "@/components/shared/layout/Header.vue";
import useAuthentication from "@/use/authentication";

const props = defineProps({
  externalLinks: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const route = useRoute();
const router = useRouter();
const { formatHexToRgb } = useFormatter();
const { getLenderConfigurationByAppId } = useApplication();
const { handleSilentSignOut } = useAuthentication();

const showSupportModal = ref<boolean>(false);
const error = ref<boolean>(false);
const loading = ref<boolean>(true);

const lid = computed<number>(() => store.state?.lenderData?.lid || route.query?.lid || 0);

const productTypeId = computed<number>(
  () => store.state.appData?.application?.productTypeId || route.query?.productTypeId || 0,
);

const programTypeId = computed<number>(
  () => store.state.appData?.application?.programTypeId || route.query?.programTypeId || 0,
);

const leadSourceId = computed(() => localStorage.getItem("leadSourceId") || route.query?.leadSourceId || 1);
const applicationTypeId = computed(() => {
  if (store?.state?.appData?.application?.metadata?.withCoApplicants !== undefined) {
    return store?.state?.appData?.application?.metadata?.withCoApplicants === true ? 2 : 1;
  } else {
    return 0;
  }
});

const lenderData = computed(() => store.state.lenderData);
const themeStyle = computed(() => {
  // SA color: #f7941e
  // Default Green: #01675a
  const themeRgb = formatHexToRgb(lenderData?.value?.primaryColor);
  const secondaryRgb = formatHexToRgb(lenderData?.value?.secondaryColor);
  const primaryColor = lenderData?.value?.primaryColor;
  const secondaryColor = lenderData?.value?.secondaryColor;

  return {
    "--theme-color": primaryColor,
    "--theme-color-rgb": `${themeRgb.r}, ${themeRgb.g}, ${themeRgb.b}`,
    "--secondary-color": secondaryColor,
    "--secondary-color-rgb": `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`,
  };
});

async function getLenderDataByQuery() {
  let lenderConfig = {} as any;

  const query = route.query as any;
  if (!query.programTypeId) {
    lenderConfig = await $api.configuration.getLenderConfigurationByLenderId(query?.lid ?? 0);
  } else {
    lenderConfig = await $api.configuration.getLenderConfiguration(
      query?.lid ?? 0,
      query.programTypeId ?? 0,
      query.productTypeId ?? 0,
    );
  }

  await store.dispatch("updateLenderData", lenderConfig);
}

async function handleAppData(appData: any, query: any) {
  if (appData && !appData?.errors) {
    // Handle redirect urls
    if (appData?.redirectUrl) {
      window.location.href = `${appData?.redirectUrl}`;
    } else {
      // handle storing lead in localStorage until ready to submit to DB
      window.localStorage.setItem("appInProgress", JSON.stringify(appData));
      await store.dispatch("setAppData", appData);
      // Clear out the query parameters
      query.appId = undefined;
      console.log("vuex state this.appData", store.state?.appData);
      const lenderConfig = await getLenderConfigurationByAppId(store.state?.appData?.application?.id);
      await store.dispatch("updateLenderData", lenderConfig);
      router.replace({ query });
    }
  }
}

async function startNewApplication(): Promise<any> {
  // only performs check on lower environments
  let query = route.query as any;
  const param = route.params as any;
  console.log("Inside startNewApplication()");

  query = { ...query, code: query.code || null };


  const appData = await $api.leads.processLead(query);

  if (appData?.redirectUrl) {
    window.location.href = `${appData?.redirectUrl}`;
  }

  localStorage.setItem("leadSourceId", query.leadSourceId ? query.leadSourceId : 0);
  localStorage.setItem("productTypeId", param.productTypeId ? param.productTypeId : query.productTypeId);

  // Sign out users and remove them when starting a new app
  await handleSilentSignOut();

  // Assumed if we get to this point they are starting a new application,
  // so we process any data passed in as a lead
  // Validate that query parameters exist in query
  await getLenderDataByQuery();
	
  return appData;
}

onMounted(async () => {
  loading.value = true;
  // TODO: Figure out how we are going to get lender information
  const query = route.query as any;
  const appInProgress = window.localStorage.getItem("appInProgress");
  let appData: AppData | undefined;

  if (query.appId) {
    console.log("Retrieving application", query.appId);
    // See if the user is trying to pull up existing application
    appData = await $api.applications.getApplication(query.appId);
  } else if (appInProgress) {
    console.log("Inside localStorage");
    // User may have an application still saved in local storage we can access
    appData = JSON.parse(appInProgress);

    if (appData?.application?.id) {
      appData = await $api.applications.getApplication(appData.application.id);
    }
    console.log("lenderid matches query?.lid", query?.lid !== appData?.application?.lenderId);
    // compare query?.lid to appData.application.lenderId (for invite flow)
    // start new application when incoming query?.lid does not match appInProgress lenderId at any point in application
    if (query?.lid !== undefined && query?.lid !== appData?.application?.lenderId) {
      console.log("clearing localstorage");
      window.localStorage.clear();
      appData = await startNewApplication();
    }
  } else {
    appData = await startNewApplication();
  }

  await handleAppData(appData, query);
  loading.value = false;
});

//#TODO add watcher on programTypeId property for application to always have latest program info

defineExpose({
  props,
});
</script>
<style lang="scss">
.application-page {
  height: 100vh;
  width: 100%;
  display: flex;

  .application-ad {
    width: calc(35% - 2em);
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background-color: var(--theme-color);
    color: #fff;
    padding: 0 2em;
    border-right: 20px solid #f0f0f0;

    .cu-logo-wrapper {
      width: 100%;

      .cu-logo {
        width: 125px;
        max-width: 95%;
      }
    }

    h1 {
      font-size: 3.5em;
    }

    .temp-id {
      position: absolute;
      top: 0;
      left: 20px;
    }
  }

  .error-message {
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    a {
      display: inline;
    }

    p {
      max-width: 500px;
      font-weight: 700;
    }
  }
}

.cuscd-dark {
  .application-page {
    .application-ad {
      color: #000100;
    }
  }
}

.application-ad-banner-left-nav {
  height: 100%;
  width: calc(4% - 2em);
  background-color: #f0f0f0;
}

.application-ad-banner {
  width: 100%;
  background: linear-gradient(-45deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0) 100%);
  background-color: var(--theme-color);
}

.application-page-footer {
  font-family: var(--body-font);
  font-size: 0.75rem; // font-size: 10px;
  font-weight: 400;
  background-color: #444444;
  color: #ffffff;
  text-align: center;
  border: solid 1px #444444;
  height: 58px;
  vertical-align: baseline;
  /* center content */
  margin: auto;
  display: flex;
  justify-content: center;
  flex-direction: column;

  footer.container {
    text-align: center;
    font-family: var(--body-font);
    font-size: 0.75rem;
    color: #ffffff;

    a {
      color: #ffffff;
    }

    p {
      margin: 0;
    }
  }
}

@media (max-width: 992px) {
  .application-page .application-ad,
  .application-page .application-ad-banner-left-nav {
    display: none;
  }

  .application-page .application-steps-wrapper {
    width: 100% !important;
  }
}
</style>
